<template>
  <SoForm v-model="isValid" @submit="addContact()">
    <div class="deceased-contact-form__container">
      <SoSelect
        v-model="contact.title"
        :items="TITLE_OPTIONS"
        label="Title"
        @update:model-value="updateField('title', $event)"
      />
      <SoTextField
        :model-value="contact.forename"
        :rules="[rules.required]"
        label="First Name*"
        type="text"
        @update:model-value="updateField('forename', $event)"
      />
      <SoTextField
        :model-value="contact.surname"
        :rules="[rules.required]"
        label="Last Name*"
        type="text"
        @update:model-value="updateField('surname', $event)"
      />
      <SoTextField
        :model-value="contact.email"
        :rules="[rules.email, rules.required]"
        label="Email*"
        type="text"
        @update:model-value="updateField('email', $event)"
      />
      <SoTextField
        :model-value="contact.phone1"
        :rules="[rules.required, rules.phoneNumber]"
        label="Landline phone*"
        @update:model-value="updateField('phone1', $event)"
      />
      <SoTextField
        :model-value="contact.phone2"
        :rules="[rules.phoneNumber]"
        label="Mobile phone"
        @update:model-value="updateField('phone2', $event)"
      />
    </div>
    <h6 class="mt-4 mb-2">Address:</h6>
    <div class="deceased-contact-form__container">
      <SoTextField
        :model-value="contact.address?.address1"
        :rules="[rules.required]"
        label="Line 1*"
        type="text"
        @update:model-value="updateAddressField('address1', $event)"
      />
      <SoTextField
        :model-value="contact.address?.address2"
        label="Line 2"
        type="text"
        @update:model-value="updateAddressField('address2', $event)"
      />
      <SoTextField
        :model-value="contact.address?.address3"
        label="Line 3"
        type="text"
        @update:model-value="updateAddressField('address3', $event)"
      />
      <SoTextField
        :model-value="contact.address?.postcode"
        :rules="[rules.postcode, rules.required]"
        label="Postcode*"
        type="text"
        @update:model-value="updateAddressField('postcode', $event)"
      />
      <SoTextField
        :model-value="address?.countryCode"
        label="Country*"
        disabled
      />
    </div>
    <SoButton
      title="Add Contact"
      type="success"
      prepend-icon="plus"
      class="my-8"
      :disabled="!isValid"
      :loading="addContactMutator.isPending"
      large
      submit
    />
  </SoForm>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { IContactForm } from "@/mutators/contact/ContactMutator.types"
  import { TITLE_OPTIONS } from "@/constants/titleOptions"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import rules from "@/validators/rules"

  const initialFormData = () =>
    ({
      isPrimary: false,
      title: "",
      forename: "",
      surname: "",
      email: "",
      phone1: "",
      phone2: "",
      contactType: "Residential",
      address: {
        address1: "",
        address2: "",
        address3: "",
        address4: null,
        address5: null,
        postcode: "",
      },
    }) as IContactForm

  export default defineComponent({
    components: {
      SoSelect,
      SoTextField,
      SoButton,
      SoForm,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Object as PropType<IContactForm>,
        default: initialFormData,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        contact: initialFormData as Partial<IContactForm>,
        addContactMutator: useCurrentAccountStore().addContact(),
        snackbar: useSnackbarStore(),
        isValid: false,
        TITLE_OPTIONS,
        rules,
      }
    },
    computed: {
      address() {
        return this.current.account.data.supplyAddress
      },
    },
    mounted() {
      this.contact = {
        ...initialFormData(),
        address: {
          address1: this.address?.addressLine1 ?? "",
          address2: this.address?.addressLine2 ?? "",
          address3: this.address?.addressLine3 ?? "",
          address4: null,
          address5: null,
          postcode: this.address?.postcode ?? "",
        },
      }
    },
    methods: {
      updateField(fieldName: string, fieldValue: string) {
        this.contact[fieldName] = fieldValue
        this.$emit("update:modelValue", this.contact)
      },
      updateAddressField(fieldName: string, fieldValue: string) {
        if (this.contact?.address) {
          this.contact.address[fieldName] = fieldValue
          this.$emit("update:modelValue", this.contact)
        }
      },
      async addContact() {
        try {
          await this.addContactMutator.mutateAsync({ ...this.contact })
          this.snackbar.addSuccess("Contact added")
        } catch (error) {
          errorReporter.report(error)
          this.snackbar.addError(
            errorHandler.userMessage(error, "Failed to add contact"),
          )
        }
      },
    },
  })
</script>

<style scoped>
  .deceased-contact-form__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
</style>
